import type {
  MeiliThought,
  MindstringPrototype,
  SelectedThought,
  SelectedThoughtFree,
} from "@/generated/fastapi";
import type { SelectedThoughtDocument, StudioLayoutMap } from "./types";
import type { StudioSituationLoadState } from "@/state/globalStoreSlices/userChoices";

export function isStudioSituationLoadedFromState({
  loadState,
  prototype,
  situationId,
}: {
  loadState: StudioSituationLoadState | undefined;
  prototype: MindstringPrototype;
  situationId: string;
}) {
  return loadState?.[prototype]?.[situationId] !== false;
}

export function filterStudioLayoutMapBySituationLoadState({
  layoutMap,
  loadState,
}: {
  layoutMap: StudioLayoutMap;
  loadState: StudioSituationLoadState | undefined;
}): StudioLayoutMap {
  return Object.fromEntries(
    Object.entries(layoutMap).map(([prototype, situationIds]) => [
      prototype,
      situationIds.filter((situationId) =>
        isStudioSituationLoadedFromState({
          loadState,
          prototype: prototype as MindstringPrototype,
          situationId,
        }),
      ),
    ]),
  ) as StudioLayoutMap;
}

export function countStudioLayoutMapSituations(
  layoutMap: StudioLayoutMap,
): Record<MindstringPrototype, number> {
  return Object.fromEntries(
    Object.entries(layoutMap).map(([prototype, situationIds]) => [
      prototype,
      situationIds.length,
    ]),
  ) as Record<MindstringPrototype, number>;
}

/**
 * Sort thoughts by semantic relevance from search results.
 * Preserves the order returned by Meili search, which reflects semantic similarity.
 */
function sortBySemanticRelevance(
  thoughts: SelectedThoughtDocument[],
  searchResults: MeiliThought[],
  selectedThought: SelectedThought,
): SelectedThoughtDocument[] {
  const relevanceMap = new Map(
    searchResults.map((thought, index) => [thought.objectID, index]),
  );

  const subset = thoughts.filter((thought) =>
    selectedThought.thoughts_ids.includes(thought.objectID),
  );

  return subset.sort((leftThought, rightThought) => {
    const leftRelevance = relevanceMap.get(leftThought.objectID) ?? Infinity;
    const rightRelevance = relevanceMap.get(rightThought.objectID) ?? Infinity;
    return leftRelevance - rightRelevance;
  });
}

/**
 * Sort thoughts by original selected order (for manual/default sorting).
 * Uses the thoughts_ids array from SelectedThought as the source of truth.
 */
function sortBySelectionOrder(
  thoughts: SelectedThoughtDocument[],
  selectedThought: SelectedThought,
): SelectedThoughtDocument[] {
  const thoughtMap = createThoughtMap(thoughts);

  return selectedThought.thoughts_ids
    .map((thoughtId) => thoughtMap.get(thoughtId))
    .filter(Boolean) as SelectedThoughtDocument[];
}

export function applyThoughtOrdering(
  thoughts: SelectedThoughtDocument[],
  searchResults: MeiliThought[],
  selectedThoughts: SelectedThought[],
  hasRerankQuery: boolean,
): SelectedThoughtDocument[] {
  const orderedThoughts: SelectedThoughtDocument[] = [];

  selectedThoughts.forEach((selection) => {
    const sortedSubset = hasRerankQuery
      ? sortBySemanticRelevance(thoughts, searchResults, selection)
      : sortBySelectionOrder(thoughts, selection);

    orderedThoughts.push(...sortedSubset);
  });

  return orderedThoughts;
}

function hasOrderChanged(oldOrder: string[], newOrder: string[]): boolean {
  if (oldOrder.length !== newOrder.length) {
    return true;
  }

  return oldOrder.some((thoughtId, index) => thoughtId !== newOrder[index]);
}

function createThoughtMap(thoughts: SelectedThoughtDocument[]) {
  return new Map(thoughts.map((thought) => [thought.objectID, thought]));
}

export function syncThoughtOrderIfChanged(
  oldOrder: string[],
  newOrder: string[],
  onReorder: (orderedThoughtIds: string[]) => void,
) {
  if (!hasOrderChanged(oldOrder, newOrder)) {
    return;
  }

  onReorder(newOrder);
}

export function rebuildSelectedThoughtsFromOrder(
  sortedThoughts: SelectedThoughtDocument[],
  originalSelectedThoughts: SelectedThought[],
): SelectedThought[] {
  const parentSelectionByThoughtId = new Map<string, SelectedThought>();
  originalSelectedThoughts.forEach((selection) =>
    selection.thoughts_ids.forEach((thoughtId) =>
      parentSelectionByThoughtId.set(thoughtId, selection),
    ),
  );

  const groupedThoughtIds = new Map<string, string[]>();
  sortedThoughts.forEach((thought) => {
    const parentSelection = parentSelectionByThoughtId.get(thought.objectID);
    if (!parentSelection) {
      return;
    }

    const groupKey = `${parentSelection.prototype}-${parentSelection.situation_id}`;
    if (!groupedThoughtIds.has(groupKey)) {
      groupedThoughtIds.set(groupKey, []);
    }

    groupedThoughtIds.get(groupKey)?.push(thought.objectID);
  });

  return originalSelectedThoughts.map((selection) => {
    const groupKey = `${selection.prototype}-${selection.situation_id}`;
    return {
      ...selection,
      thoughts_ids: groupedThoughtIds.get(groupKey) ?? selection.thoughts_ids,
    };
  });
}

export function syncSelectedThoughtsFreeOrderIfChanged(
  oldOrder: string[],
  newOrder: string[],
  onReorder: (orderedThoughtIds: string[]) => void,
) {
  if (!hasOrderChanged(oldOrder, newOrder)) {
    return;
  }

  onReorder(newOrder);
}

export function rebuildSelectedThoughtsFreeFromOrder(
  sortedThoughts: SelectedThoughtDocument[],
  originalSelectedThoughtsFree: SelectedThoughtFree[],
): SelectedThoughtFree[] {
  const parentSelectionByThoughtId = new Map<string, SelectedThoughtFree>();
  originalSelectedThoughtsFree.forEach((selection) =>
    selection.thoughts_ids.forEach((thoughtId) =>
      parentSelectionByThoughtId.set(thoughtId, selection),
    ),
  );

  const groupedThoughtIds = new Map<string, string[]>();
  sortedThoughts.forEach((thought) => {
    const parentSelection = parentSelectionByThoughtId.get(thought.objectID);
    if (!parentSelection) {
      return;
    }

    const groupKey = parentSelection.prototype;
    if (!groupedThoughtIds.has(groupKey)) {
      groupedThoughtIds.set(groupKey, []);
    }

    groupedThoughtIds.get(groupKey)?.push(thought.objectID);
  });

  return originalSelectedThoughtsFree.map((selection) => ({
    ...selection,
    thoughts_ids:
      groupedThoughtIds.get(selection.prototype) ?? selection.thoughts_ids,
  }));
}
